import { useState, useRef, useCallback, useEffect } from "react";

export interface UseVoiceRecorderReturn {
  isRecording: boolean;
  isStartingRecording: boolean;
  recordingTime: number;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<Blob | null>;
  error: string | null;
  hasPermission: boolean | null;
}

let recorderIdCounter = 0;

export const useVoiceRecorder = (): UseVoiceRecorderReturn => {
  const [isRecording, setIsRecording] = useState(false);
  const [isStartingRecording, setIsStartingRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recorderIdRef = useRef<number>(0);

  const checkPermission = useCallback(async (): Promise<boolean> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      setHasPermission(true);
      setError(null);
      return true;
    } catch (err: any) {
      setHasPermission(false);
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        setError("マイクの使用許可が必要です。ブラウザの設定でマイクへのアクセスを許可してください。");
      } else if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
        setError("マイクが見つかりません。マイクが接続されているか確認してください。");
      } else {
        setError("マイクへのアクセスに失敗しました。");
      }
      return false;
    }
  }, []);

  const startRecording = useCallback(async (): Promise<void> => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      return;
    }

    setIsStartingRecording(true);
    try {
      setError(null);

      if (!hasPermission) {
        const permissionGranted = await checkPermission();
        if (!permissionGranted) {
          return;
        }
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000,
          channelCount: 1,
        },
      });

      streamRef.current = stream;

      const mimeTypes = [
        "audio/webm",
        "audio/webm;codecs=opus",
        "audio/ogg;codecs=opus",
        "audio/mp4",
        "audio/wav",
      ];

      let selectedMimeType = "";
      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType;
          break;
        }
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: selectedMimeType || undefined,
      });

      recorderIdCounter++;
      const currentRecorderId = recorderIdCounter;
      recorderIdRef.current = currentRecorderId;

      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      return new Promise<void>((resolve, reject) => {
        mediaRecorder.onstart = () => {
          setRecordingTime(0);
          setIsRecording(true);

          if (timerRef.current) {
            clearInterval(timerRef.current);
          }

          timerRef.current = setInterval(() => {
            setRecordingTime((prev) => prev + 1);
          }, 1000);

          resolve();
        };

        mediaRecorder.onerror = (event: any) => {
          console.error(`[Recorder ${currentRecorderId}] エラー:`, event);
          setError("録音中にエラーが発生しました。");
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          setIsRecording(false);
          reject(event);
        };

        mediaRecorder.onstop = () => {
        };

        try {
          mediaRecorder.start(100);
        } catch (err) {
          console.error(`[Recorder ${currentRecorderId}] start()エラー:`, err);
          reject(err);
        }
      });
    } catch (err: any) {
      setError("録音の開始に失敗しました。");
      console.error("Failed to start recording:", err);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setIsRecording(false);
    } finally {
      setIsStartingRecording(false);
    }
  }, [hasPermission, checkPermission]);

  const stopRecording = useCallback(async (): Promise<Blob | null> => {
    const currentRecorderId = recorderIdRef.current;

    return new Promise((resolve) => {
      const mediaRecorder = mediaRecorderRef.current;
      
      if (!mediaRecorder) {
        console.warn(`[Recorder ${currentRecorderId}] MediaRecorderが存在しません`);
        resolve(null);
        return;
      }

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      const cleanup = () => {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }
        mediaRecorderRef.current = null;
        chunksRef.current = [];
        setIsRecording(false);
        setRecordingTime(0);
      };

      const originalOnstop = mediaRecorder.onstop;
      mediaRecorder.onstop = () => {
        if (originalOnstop) {
          originalOnstop.call(mediaRecorder, new Event('stop'));
        }
        
        if (chunksRef.current.length > 0) {
          const blob = new Blob(chunksRef.current, {
            type: mediaRecorder.mimeType || "audio/webm",
          });
          cleanup();
          resolve(blob);
        } else {
          cleanup();
          resolve(null);
        }
      };

      if (mediaRecorder.state === "recording") {
        mediaRecorder.stop();
      } else {
        cleanup();
        resolve(null);
      }
    });
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearInterval(timerRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  return {
    isRecording,
    isStartingRecording,
    recordingTime,
    startRecording,
    stopRecording,
    error,
    hasPermission,
  };
};
